/**
 * Las hojas carta que están en pantalla, como PDF para mandar (2026-10-19).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo pidió el dueño en una capacitación: «al darle en enviar, no envíe el
 * texto, sino el PDF». Una cotización se pide por teléfono y se manda por
 * WhatsApp, y quien la recibe la reenvía a quien decide: tiene que llegar el
 * papel, no un mensaje con renglones.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO: COPIANDO LO QUE EL NAVEGADOR YA DIBUJÓ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La hoja carta es HTML (`HojaCarta`), y se imprime con el diálogo del
 * navegador, que no sabe entregar un archivo. Así que se lee la hoja ya
 * dibujada: dónde quedó cada palabra, cada recuadro, cada sombreado y el logo,
 * y se escribe lo mismo en un PDF, en el mismo sitio. Cada palabra va donde el
 * navegador la puso, así que los cortes de renglón, las columnas a la derecha
 * y el reparto en hojas son exactamente los de la pantalla.
 *
 * Igual que el PDF del rollo (`reciboPdf.ts`), escrito a mano y sin librería:
 * Helvetica es una de las catorce letras que todo visor de PDF trae (mide lo
 * mismo que la Arial con la que se ve en Windows), y el texto va en WinAnsi,
 * que cubre el español. El logo va como JPEG, que el PDF entiende tal cual.
 * Una cotización de una hoja pesa unos pocos KB más el logo.
 *
 * Sirve para cualquier papel hecho con `HojaCarta`: se le da el `id` de la hoja.
 */

import { CLASE_HOJA_CARTA, HOJA_CARTA_PX } from "./imprimirHoja";
import { aWinAnsi, escapar } from "./reciboPdf";

/** Un píxel de CSS son 0,75 puntos de PDF (96 contra 72 por pulgada). */
const PT = 0.75;
const ANCHO_PT = HOJA_CARTA_PX.ancho * PT; // 612: 8,5 pulgadas
const ALTO_PT = HOJA_CARTA_PX.alto * PT; // 792: 11 pulgadas

/**
 * Lo que la letra baja de la línea de base, como fracción del cuerpo. El
 * navegador da la caja de cada palabra; la base queda ese tanto por encima del
 * borde de abajo (Arial 0,212 y Helvetica Neue 0,213: da igual cuál usó).
 */
const DESCENSO = 0.212;

type Color = [number, number, number];
type Imagen = { nombre: string; ancho: number; alto: number; jpeg: string };
/**
 * Lo que dibuja una hoja y los nombres de las imágenes que usa. Sin medida, es
 * una hoja carta; quien llame puede dar otra.
 */
export type Pagina = { contenido: string; usadas: string[]; anchoPt?: number; altoPt?: number };
/** Las imágenes de todo el documento, una vez cada una: el logo se repite en cada hoja. */
type Imagenes = Map<string, Imagen>;

export const n = (x: number) => (Math.round(x * 100) / 100).toString();

/** «rgb(17, 24, 39)» → [0,067, 0,094, 0,153]. `null` si es transparente. */
export function color(css: string): Color | null {
  const m = css.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const partes = m[1]!.split(/[\s,/]+/).filter(Boolean).map(Number);
  if (partes.length < 3 || (partes.length >= 4 && partes[3]! === 0)) return null;
  return [partes[0]! / 255, partes[1]! / 255, partes[2]! / 255];
}

export const esBlanco = (c: Color) => c[0] > 0.99 && c[1] > 0.99 && c[2] > 0.99;
export const rgb = (c: Color, relleno: boolean) => `${n(c[0])} ${n(c[1])} ${n(c[2])} ${relleno ? "rg" : "RG"}`;

/** Una imagen ya cargada, en JPEG sobre blanco. `null` si el navegador no deja leerla. */
function aJpeg(img: HTMLImageElement, ancho: number, alto: number): Omit<Imagen, "nombre"> | null {
  if (!img.complete || !img.naturalWidth) return null;
  try {
    // Al doble de lo que ocupa en la hoja, para que no se vea borrosa.
    const w = Math.max(1, Math.round(ancho * 2));
    const h = Math.max(1, Math.round(alto * 2));
    const lienzo = document.createElement("canvas");
    lienzo.width = w;
    lienzo.height = h;
    const ctx = lienzo.getContext("2d");
    if (!ctx) return null;
    // El JPEG no tiene transparencia: un logo PNG o WebP sin fondo, sobre blanco.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const datos = lienzo.toDataURL("image/jpeg", 0.92);
    const base64 = datos.slice(datos.indexOf(",") + 1);
    return { ancho: w, alto: h, jpeg: atob(base64) };
  } catch {
    // Una imagen de otro sitio «mancha» el lienzo y no se puede leer: sin logo.
    return null;
  }
}

/** Lo que dibuja una hoja, en órdenes de PDF. */
function dibujarHoja(hoja: HTMLElement, imagenes: Imagenes): Pagina {
  const base = hoja.getBoundingClientRect();
  // En pantalla la hoja va reducida: todo se lleva a su tamaño real.
  const k = HOJA_CARTA_PX.ancho / base.width;
  const caja = (r: DOMRect) => ({
    x: (r.left - base.left) * k * PT,
    // El PDF cuenta desde abajo.
    y: ALTO_PT - (r.bottom - base.top) * k * PT,
    w: r.width * k * PT,
    h: r.height * k * PT,
  });

  const fondos: string[] = [];
  const usadas = new Set<string>();
  const dibujos: string[] = [];
  const textos: string[] = [];

  // ── Recuadros, sombreados e imágenes ──────────────────────────────────────
  for (const el of [hoja, ...Array.from(hoja.querySelectorAll<HTMLElement>("*"))]) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) continue;
    const e = getComputedStyle(el);
    if (e.visibility === "hidden" || e.display === "none") continue;
    const b = caja(r);

    const fondo = color(e.backgroundColor);
    if (fondo && !esBlanco(fondo)) fondos.push(`${rgb(fondo, true)} ${n(b.x)} ${n(b.y)} ${n(b.w)} ${n(b.h)} re f`);

    // Cada borde, como una franja de su grosor: sale igual en cualquier visor.
    type Franja = (g: number) => [number, number, number, number];
    const lados: [string, Franja][] = [
      ["Top", (g) => [b.x, b.y + b.h - g, b.w, g]],
      ["Bottom", (g) => [b.x, b.y, b.w, g]],
      ["Left", (g) => [b.x, b.y, g, b.h]],
      ["Right", (g) => [b.x + b.w - g, b.y, g, b.h]],
    ];
    for (const [lado, franja] of lados) {
      const grosor = parseFloat(e.getPropertyValue(`border-${lado.toLowerCase()}-width`)) || 0;
      if (grosor <= 0 || e.getPropertyValue(`border-${lado.toLowerCase()}-style`) === "none") continue;
      const c = color(e.getPropertyValue(`border-${lado.toLowerCase()}-color`));
      if (!c) continue;
      // El grosor ya viene a tamaño real (el estilo no sabe de la escala).
      const [x, y, w, h] = franja(grosor * PT);
      dibujos.push(`${rgb(c, true)} ${n(x)} ${n(y)} ${n(w)} ${n(h)} re f`);
    }

    if (el instanceof HTMLImageElement) {
      const clave = `${el.currentSrc || el.src}|${Math.round(r.width * k)}x${Math.round(r.height * k)}`;
      let imagen = imagenes.get(clave);
      if (!imagen) {
        const jpeg = aJpeg(el, r.width * k, r.height * k);
        if (jpeg) imagenes.set(clave, (imagen = { nombre: `Im${imagenes.size + 1}`, ...jpeg }));
      }
      if (imagen) {
        usadas.add(imagen.nombre);
        dibujos.push(`q ${n(b.w)} 0 0 ${n(b.h)} ${n(b.x)} ${n(b.y)} cm /${imagen.nombre} Do Q`);
      }
    }
  }

  // ── El texto, palabra por palabra donde la dejó el navegador ─────────────
  const recorrido = document.createTreeWalker(hoja, NodeFilter.SHOW_TEXT);
  const rango = document.createRange();
  let fuente = "";
  let tinta = "";
  let espaciado = "";
  for (let nodo = recorrido.nextNode() as Text | null; nodo; nodo = recorrido.nextNode() as Text | null) {
    const padre = nodo.parentElement;
    if (!padre || !nodo.data.trim()) continue;
    const e = getComputedStyle(padre);
    if (e.visibility === "hidden") continue;

    const cuerpo = parseFloat(e.fontSize) || 11.5;
    const negrita = (parseInt(e.fontWeight, 10) || 400) >= 600;
    const mayusculas = e.textTransform === "uppercase";
    const letra = `/${negrita ? "F2" : "F1"} ${n(cuerpo * PT)} Tf`;
    const c = color(e.color) ?? [0, 0, 0];
    const conTinta = rgb(c, true);
    const tc = `${n((parseFloat(e.letterSpacing) || 0) * PT)} Tc`;

    const escribir = (texto: string, r: DOMRect) => {
      if (r.width === 0 || r.height === 0) return;
      if (letra !== fuente) textos.push((fuente = letra));
      if (conTinta !== tinta) textos.push((tinta = conTinta));
      if (tc !== espaciado) textos.push((espaciado = tc));
      const x = (r.left - base.left) * k * PT;
      const y = ALTO_PT - ((r.bottom - base.top) * k - DESCENSO * cuerpo) * PT;
      // El signo menos de los descuentos («−5,00») no está en WinAnsi: guion.
      const limpio = texto.replace(/[−‐‑]/g, "-");
      const visible = mayusculas ? limpio.toLocaleUpperCase("es") : limpio;
      textos.push(`1 0 0 1 ${n(x)} ${n(y)} Tm (${escapar(aWinAnsi(visible))}) Tj`);
    };

    for (const palabra of nodo.data.matchAll(/\S+/g)) {
      const desde = palabra.index ?? 0;
      rango.setStart(nodo, desde);
      rango.setEnd(nodo, desde + palabra[0].length);
      const cajas = rango.getClientRects();
      if (cajas.length === 1) {
        escribir(palabra[0], cajas[0]!);
        continue;
      }
      // Partida en dos renglones (un código largo sin espacios): letra a letra.
      const letras = Array.from(palabra[0]);
      let i = desde;
      for (const l of letras) {
        rango.setStart(nodo, i);
        rango.setEnd(nodo, i + l.length);
        const r = rango.getClientRects()[0];
        if (r) escribir(l, r);
        i += l.length;
      }
    }
  }

  const contenido = [
    ...fondos,
    ...dibujos,
    textos.length ? ["BT", ...textos, "ET"].join("\n") : "",
  ].join("\n");
  return { contenido, usadas: [...usadas] };
}

/** Arma el archivo: el catálogo, las páginas, las dos letras y las imágenes. */
export function armar(paginas: Pagina[], imagenes: Imagenes = new Map()): Blob {
  // 1 catálogo, 2 páginas, 3 y 4 las letras; luego las imágenes, una vez cada
  // una; luego, por hoja, su página y su contenido.
  const objetos: string[] = [];
  const kids: number[] = [];
  let siguiente = 5;
  const objImagen = new Map<string, number>();
  for (const i of imagenes.values()) {
    objImagen.set(i.nombre, siguiente);
    objetos[siguiente++] =
      `<< /Type /XObject /Subtype /Image /Width ${i.ancho} /Height ${i.alto} /ColorSpace /DeviceRGB ` +
      `/BitsPerComponent 8 /Filter /DCTDecode /Length ${i.jpeg.length} >>\nstream\n${i.jpeg}\nendstream`;
  }
  const porHoja = paginas.map((p) => {
    const objPagina = siguiente++;
    const objContenido = siguiente++;
    kids.push(objPagina);
    return { ...p, objPagina, objContenido };
  });

  objetos[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objetos[2] = `<< /Type /Pages /Kids [${kids.map((k) => `${k} 0 R`).join(" ")}] /Count ${kids.length} >>`;
  objetos[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>";
  objetos[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>";
  for (const h of porHoja) {
    const xobjetos = h.usadas.length
      ? ` /XObject << ${h.usadas.map((nombre) => `/${nombre} ${objImagen.get(nombre)} 0 R`).join(" ")} >>`
      : "";
    objetos[h.objPagina] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${n(h.anchoPt ?? ANCHO_PT)} ${n(h.altoPt ?? ALTO_PT)}] ` +
      `/Resources << /Font << /F1 3 0 R /F2 4 0 R >>${xobjetos} >> /Contents ${h.objContenido} 0 R >>`;
    objetos[h.objContenido] = `<< /Length ${h.contenido.length} >>\nstream\n${h.contenido}\nendstream`;
  }

  // ⚠️ La tabla de posiciones cuadra al byte: se mide lo ya escrito (ver
  // `reciboComoPdf`). Todo es «un carácter, un byte»: el texto ya va en
  // WinAnsi y el JPEG, byte a byte.
  let pdf = "%PDF-1.4\n%\xe2\xe3\xcf\xd3\n";
  const posiciones: number[] = [];
  for (let i = 1; i < objetos.length; i++) {
    posiciones[i] = pdf.length;
    pdf += `${i} 0 obj\n${objetos[i]}\nendobj\n`;
  }
  const inicioTabla = pdf.length;
  pdf += `xref\n0 ${objetos.length}\n0000000000 65535 f \n`;
  for (let i = 1; i < objetos.length; i++) pdf += `${String(posiciones[i]).padStart(10, "0")} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objetos.length} /Root 1 0 R >>\nstartxref\n${inicioTabla}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * Las hojas de la `HojaCarta` con ese `id`, como PDF. `null` si no está en la
 * pantalla.
 */
export function hojasCartaComoPdf(idDelElemento: string): Blob | null {
  const raiz = document.getElementById(idDelElemento);
  if (!raiz) return null;
  const hojas = Array.from(raiz.querySelectorAll<HTMLElement>(`.${CLASE_HOJA_CARTA}`));
  if (hojas.length === 0) return null;
  const imagenes: Imagenes = new Map();
  const paginas = hojas.map((h) => dibujarHoja(h, imagenes));
  return armar(paginas, imagenes);
}
